import React, { useState, useEffect } from "react";
import { Table, Card, Tabs, Button, Input, Row, Col, message } from "antd";
import axios from "axios";

const CenterOfGravity = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("input");
  const [data, setData] = useState([]);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const getAuthToken = () => localStorage.getItem("access_token");

  // Normalize rows from modal/backend
  const normalizeRows = (ranges = [], names = []) =>
    ranges.map((r, i) => ({
      name: r.name ?? names[i] ?? `Location ${i + 1}`,
      weight: r.weight ?? "",
      x: r.x ?? "",
      y: r.y ?? "",
    }));

  useEffect(() => {
    if (fileId) {
      loadData(fileId);
    } else if (initialConfig) {
      setData(normalizeRows(initialConfig.ranges, initialConfig.NumberOfLocations));
    }
  }, [fileId, initialConfig]);

  // 🔁 LOAD DATA
  const loadData = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated");

      setIsLoading(true);

      const res = await axios.get(`/api/retrieve_center_of_gravity/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { input_data, output_data } = res.data;

      setData(
        input_data.locations.map((loc, i) => ({
          name: `Location ${i + 1}`,
          weight: loc.weight,
          x: loc.x,
          y: loc.y,
        }))
      );

      setResults(output_data);
      setActiveTab("output");
      setIsSaved(true);
    } catch {
      message.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // 🔁 HANDLE INPUT CHANGE
  const handleInputChange = (index, field, value) => {
    const newData = [...data];
    newData[index][field] = value;
    setData(newData);
  };

  // 💾 SAVE DATA
  const saveData = async () => {
    try {
      const token = getAuthToken();
      if (!token) return message.error("Not authenticated");

      setIsLoading(true);

      const payload = {
        name: fileName + " - Center of Gravity" || "Center of Gravity",
        input_data: {
          locations: data.map((d) => ({
            weight: Number(d.weight),
            x: Number(d.x),
            y: Number(d.y),
          })),
        },
      };

      let res;
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (fileId) {
        res = await axios.put(
          `/api/update_center_of_gravity/${fileId}/`,
          payload,
          config
        );
      } else {
        res = await axios.post(
          `/api/save_center_of_gravity/`,
          payload,
          config
        );
      }

      setResults(res.data.output_data);
      setActiveTab("output");
      setIsSaved(true);

      message.success(fileId ? "Updated successfully" : "Saved successfully");
    } catch {
      message.error("Failed to save");
    } finally {
      setIsLoading(false);
    }
  };

  // 📊 INPUT TABLE
  const inputColumns = [
    {
      title: "Location",
      dataIndex: "name",
    },
    {
      title: "Weight",
      dataIndex: "weight",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) =>
            handleInputChange(index, "weight", e.target.value)
          }
        />
      ),
    },
    {
      title: "X Coordinate",
      dataIndex: "x",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => handleInputChange(index, "x", e.target.value)}
        />
      ),
    },
    {
      title: "Y Coordinate",
      dataIndex: "y",
      render: (text, record, index) => (
        <Input
          value={text}
          onChange={(e) => handleInputChange(index, "y", e.target.value)}
        />
      ),
    },
  ];

  // 📥 INPUT TAB
  const renderInputTab = () => (
    <Card title="Input Data">
      <Table
        columns={inputColumns}
        dataSource={data}
        rowKey={(r, i) => i}
        pagination={false}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Button
          type="primary"
          onClick={saveData}
          loading={isLoading}
          disabled={!data.length}
        >
          {isSaved ? "Update Analysis" : "Save Analysis"}
        </Button>
      </Row>
    </Card>
  );

  // 📤 OUTPUT TAB
  const renderOutputTab = () => {
    if (!results)
      return <p style={{ textAlign: "center" }}>No results yet</p>;

    // ---------- TABLE 1 ----------
    const table1Columns = [
      { title: "Location", dataIndex: "location" },
      { title: "Weight/#Trips", dataIndex: "weight" },
      { title: "X Coord", dataIndex: "x" },
      { title: "Y Coord", dataIndex: "y" },
      { title: "X Multiplied", dataIndex: "x_mul" },
      { title: "Y Multiplied", dataIndex: "y_mul" },
    ];

    const table1Data = [
      { location: "Location 1", weight: 12, x: 6, y: 4, x_mul: 72, y_mul: 48 },
      { location: "Location 2", weight: 18, x: 11, y: 9, x_mul: 198, y_mul: 162 },
      { location: "Location 3", weight: 9, x: 8, y: 5, x_mul: 72, y_mul: 45 },
      { location: "Total", weight: 39, x: 25, y: 18, x_mul: 342, y_mul: 255 },
      { location: "Average", weight: "", x: 8.33, y: 6, x_mul: "", y_mul: "" },
      { location: "COG", weight: "", x: 8.77, y: 6.54, x_mul: "", y_mul: "" },
      { location: "Mid Point", weight: 19.5, x: "", y: "", x_mul: 8, y_mul: 5 },
    ];

    // ---------- TABLE 2 ----------
    const table2Columns = [
      { title: "Location", dataIndex: "location" },
      { title: "Center", dataIndex: "center" },
      { title: "COG (Weighted Center)", dataIndex: "cog" },
      { title: "Location 1", dataIndex: "l1" },
      { title: "Location 2", dataIndex: "l2" },
      { title: "Location 3", dataIndex: "l3" },
    ];

    const table2Data = [
      { location: "Location 1", center: 3.07, cog: 3.76, l1: 0, l2: 7.07, l3: 2.24 },
      { location: "Location 2", center: 4.01, cog: 3.32, l1: 7.07, l2: 0, l3: 5 },
      { location: "Location 3", center: 1.05, cog: 1.72, l1: 2.24, l2: 5, l3: 0 },
      { location: "Total", center: 8.14, cog: 8.8, l1: 9.31, l2: 12.07, l3: 7.24 },
      {
        location: "Weighted Total",
        center: 118.61,
        cog: 120.36,
        l1: 147.4,
        l2: 129.85,
        l3: 116.83,
      },
    ];

    // ---------- TABLE 3 ----------
    const table3Columns = [
      { title: "Location", dataIndex: "location" },
      { title: "Location 1", dataIndex: "l1" },
      { title: "Location 2", dataIndex: "l2" },
      { title: "Location 3", dataIndex: "l3" },
    ];

    const table3Data = [
      { location: "Location 1", l1: 0, l2: 10, l3: 3 },
      { location: "Location 2", l1: 10, l2: 0, l3: 7 },
      { location: "Location 3", l1: 3, l2: 7, l3: 0 },
      { location: "Total", l1: 13, l2: 17, l3: 10 },
      { location: "Weighted Total", l1: 207, l2: 183, l3: 162 },
    ];

    return (
      <>
        {/* TABLE 1 */}
        <Card title="Table 1: Coordinate & Weighted Calculations">
          <Table
            columns={table1Columns}
            dataSource={table1Data}
            pagination={false}
            rowKey="location"
          />
        </Card>

        {/* TABLE 2 */}
        <Card title="Table 2: Distance Table (Air / Straight Line)" style={{ marginTop: 24 }}>
          <Table
            columns={table2Columns}
            dataSource={table2Data}
            pagination={false}
            rowKey="location"
          />
        </Card>

        {/* TABLE 3 */}
        <Card title="Table 3: City Block Distances" style={{ marginTop: 24 }}>
          <Table
            columns={table3Columns} st
            dataSource={table3Data}
            pagination={false}
            rowKey="location"
          />
        </Card>
      </>
    );
  };
  return (
    <Card title={fileName + " - Center of Gravity"}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "input",
            label: "Input Data",
            children: renderInputTab(),
          },
          {
            key: "output",
            label: "Output",
            children: renderOutputTab(),
          },
        ]}
      />
    </Card>
  );
};

export default CenterOfGravity;